import { useState } from "react";
import { Loader2, Search, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { aiOn, useSettings } from "@/context/SettingsContext";

export const ReminderSearch = ({ q, setQ, ai, setAi, aiEnabled }) => {
  const [busy, setBusy] = useState(false);
  const runAi = async () => {
    if (q.trim().length < 2) return toast.error("Type what you're looking for first");
    setBusy(true);
    try { setAi((await api.post("/ai/search-reminders", { query: q }, { timeout: 60000 })).data.results); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
    return undefined;
  };
  return (
    <div className="mb-5 flex max-w-4xl flex-wrap items-center gap-2" data-testid="reminder-search">
      <div className="relative min-w-[220px] flex-1">
        <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input value={q} onChange={(e) => { setQ(e.target.value); setAi(null); }} onKeyDown={(e) => e.key === "Enter" && aiEnabled && runAi()}
          placeholder={aiEnabled ? "Search, or describe it and press Enter for AI search…" : "Search reminders…"}
          className="h-10 w-full rounded-full border bg-card/60 pl-10 pr-9 text-sm outline-none focus:border-white/30" data-testid="reminder-search-input" />
        {q && <button onClick={() => { setQ(""); setAi(null); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" data-testid="reminder-search-clear"><X className="h-4 w-4" /></button>}
      </div>
      {aiEnabled && (
        <button onClick={runAi} disabled={busy} className="flex h-10 items-center gap-1.5 rounded-full bg-cyan-400 px-4 text-sm font-semibold text-black transition-colors hover:bg-cyan-300 disabled:opacity-60" data-testid="reminder-ai-search-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} AI search
        </button>
      )}
    </div>
  );
};

const text = (r) => [r.title, r.details, ...(r.updates || []).map((u) => u.body), ...(r.checklist || []).map((c) => c.text)].join(" ").toLowerCase();

export const useReminderSearch = (items) => {
  const { team } = useSettings();
  const [q, setQ] = useState("");
  const [ai, setAi] = useState(null);
  let shown = items;
  if (items && ai) {
    const byId = Object.fromEntries(items.map((r) => [r.id, r]));
    shown = ai.filter((x) => byId[x.id]).map((x) => ({ ...byId[x.id], ai_why: x.why }));
  } else if (items && q.trim()) {
    const words = q.toLowerCase().split(/\s+/).filter(Boolean);
    shown = items.filter((r) => { const t = text(r); return words.every((w) => t.includes(w)); });
  }
  return { q, setQ, ai, setAi, shown, aiEnabled: aiOn(team, "search") };
};
